import React, { useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  buildPipelineTree,
  VARIANT,
  VARIANT_LABEL,
} from "../pipelines";
import {
  formatCompactNumber,
  formatMoney,
  formatNumber,
  formatPercent,
  shareOf,
} from "../format";

const VARIANT_CLASS = {
  [VARIANT.PRIMARY]: "primary",
  [VARIANT.RETRY]: "retry",
  [VARIANT.FALLBACK]: "fallback",
  [VARIANT.VERIFICATION]: "verification",
};

function VariantTag({ variant }) {
  return (
    <span className={`co-variant co-variant--${VARIANT_CLASS[variant]}`}>
      {VARIANT_LABEL[variant]}
    </span>
  );
}

/**
 * Pipeline -> step -> variant, with retry/fallback spend called out as
 * overhead. Overhead is the number worth watching: it is money spent redoing
 * work that was already paid for once.
 */
export function PipelineBreakdown({ features, onSelectFeature }) {
  const [openPipeline, setOpenPipeline] = useState(null);
  const [openStep, setOpenStep] = useState(null);

  const tree = useMemo(
    () =>
      buildPipelineTree(
        (features || []).map((f) => ({
          traceName: f.traceName,
          cost: f.summary?.totalCost || 0,
          runs: f.summary?.traceCount || 0,
          tokens: f.summary?.totalTokens || 0,
        })),
      ),
    [features],
  );

  const total = tree.reduce((sum, p) => sum + p.cost, 0);
  const totalOverhead = tree.reduce((sum, p) => sum + p.overheadCost, 0);

  if (!tree.length) {
    return <p className="co-empty">No pipeline data in this window.</p>;
  }

  return (
    <div className="co-pipelines">
      {totalOverhead > 0 ? (
        <div className="co-overhead-banner">
          <ReplayIcon fontSize="small" />
          <span>
            <strong>{formatMoney(totalOverhead)}</strong> of{" "}
            {formatMoney(total)} ({formatPercent(shareOf(totalOverhead, total))})
            went to retries and provider fallbacks — work that was already paid
            for at least once.
          </span>
        </div>
      ) : null}

      {tree.map((pipeline) => {
        const isOpen = openPipeline === pipeline.id;
        return (
          <div className="co-pipeline" key={pipeline.id}>
            <button
              type="button"
              className="co-pipeline-head"
              onClick={() => setOpenPipeline(isOpen ? null : pipeline.id)}
            >
              {isOpen ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
              <div className="co-pipeline-title">
                <span className="co-pipeline-name">{pipeline.label}</span>
                <span className="co-pipeline-desc">{pipeline.description}</span>
              </div>
              <div className="co-pipeline-stats">
                <div className="co-pipeline-cost">{formatMoney(pipeline.cost)}</div>
                <div className="co-pipeline-sub">
                  {formatPercent(shareOf(pipeline.cost, total))} of AI spend ·{" "}
                  {formatNumber(pipeline.runs)} runs
                  {pipeline.overheadCost > 0 ? (
                    <>
                      {" · "}
                      <span className="co-overhead-inline">
                        {formatMoney(pipeline.overheadCost)} overhead
                      </span>
                    </>
                  ) : null}
                </div>
              </div>
            </button>

            {isOpen ? (
              <div className="co-steps">
                {pipeline.steps.map((step, index) => {
                  const stepKey = `${pipeline.id}:${step.id}`;
                  const stepOpen = openStep === stepKey;
                  const overhead = step.variants
                    .filter(
                      (v) =>
                        v.variant === VARIANT.RETRY ||
                        v.variant === VARIANT.FALLBACK,
                    )
                    .reduce((sum, v) => sum + (v.cost || 0), 0);

                  return (
                    <div className="co-step" key={stepKey}>
                      <button
                        type="button"
                        className="co-step-head"
                        onClick={() => setOpenStep(stepOpen ? null : stepKey)}
                      >
                        <span className="co-step-index">{index + 1}</span>
                        <span className="co-step-label">
                          {step.label}
                          {!step.catalogued ? (
                            <span
                              className="co-uncat"
                              title="This trace name has no entry in pipelines.js — add it so it is attributed properly."
                            >
                              uncatalogued
                            </span>
                          ) : null}
                        </span>
                        <span className="co-step-bar-wrap">
                          <span
                            className="co-step-bar"
                            style={{
                              width: `${Math.min(shareOf(step.cost, pipeline.cost) * 100, 100)}%`,
                            }}
                          />
                        </span>
                        <span className="co-step-cost">
                          {formatMoney(step.cost)}
                        </span>
                        <span className="co-step-share">
                          {formatPercent(shareOf(step.cost, pipeline.cost))}
                        </span>
                        <span className="co-step-runs">
                          {formatNumber(step.runs)} runs
                        </span>
                        <span className="co-step-avg">
                          {step.runs
                            ? `${formatMoney(step.cost / step.runs)}/run`
                            : "—"}
                        </span>
                      </button>

                      {stepOpen ? (
                        <div className="co-variants">
                          {overhead > 0 ? (
                            <p className="co-note co-note--warn">
                              {formatMoney(overhead)} of this step (
                              {formatPercent(shareOf(overhead, step.cost))}) is
                              retry / fallback overhead.
                            </p>
                          ) : null}
                          <table className="ou-table co-inner-table">
                            <thead>
                              <tr>
                                <th>Trace name</th>
                                <th>Type</th>
                                <th>Cost</th>
                                <th>Share of step</th>
                                <th>Runs</th>
                                <th>Avg $/run</th>
                                <th>Tokens</th>
                              </tr>
                            </thead>
                            <tbody>
                              {step.variants.map((v) => (
                                <tr
                                  key={v.traceName}
                                  className={onSelectFeature ? "is-clickable" : ""}
                                  onClick={() =>
                                    onSelectFeature && onSelectFeature(v.traceName)
                                  }
                                >
                                  <td className="co-mono">{v.traceName}</td>
                                  <td>
                                    <VariantTag variant={v.variant} />
                                  </td>
                                  <td>{formatMoney(v.cost)}</td>
                                  <td>
                                    {formatPercent(shareOf(v.cost, step.cost))}
                                  </td>
                                  <td>{formatNumber(v.runs)}</td>
                                  <td>
                                    {v.runs ? formatMoney(v.cost / v.runs) : "—"}
                                  </td>
                                  <td>{formatCompactNumber(v.tokens)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
